import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, logout } from '../services/AuthService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const interviewTypes = [
  '1st Round',
  'Technical',
  'HR',
  'Final',
  'Managerial',
  'System Design',
  'Behavioral',
];

const DEFAULT_FORM = {
  candidateName: '',
  presentRole: '',
  interviewingRole: '',
  interviewingClient: '',
  currentClient: '',
  previousClient: '',
  previousClient2: '',
  yearsOfExperience: '10',
  interviewType: '1st Round',
  jobDescription: '',
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0d1117',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '30px 16px 60px',
  },
  wrapper: {
    width: '100%',
    maxWidth: '720px',
  },
  /* ── top-right hamburger (matches reference) ── */
  menuBtn: {
    position: 'fixed',
    top: '14px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: '#8b949e',
    fontSize: '22px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '4px',
  },
  card: {
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '12px',
    padding: 'clamp(20px,4vw,28px) clamp(16px,4vw,32px) clamp(24px,4vw,32px)',
    marginBottom: '0',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '19px',
    fontWeight: '700',
    color: '#e6edf3',
    marginBottom: '24px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
    gap: '14px 20px',
  },
  fieldFull: {
    gridColumn: '1 / -1',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: '#cdd9e5',
    marginBottom: '6px',
    fontWeight: '600',
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#e6edf3',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '9px 12px',
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#e6edf3',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '9px 12px',
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#e6edf3',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '110px',
    boxSizing: 'border-box',
  },
  btnRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '22px',
  },
  startBtn: {
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 28px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  orRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#8b949e',
    fontSize: '13px',
    margin: '20px 0 8px',
  },
  orLine: {
    flex: 1,
    height: '1px',
    background: '#30363d',
    display: 'block',
  },
  codeHint: {
    color: '#8b949e',
    fontSize: '12px',
    marginBottom: '10px',
  },
  codeRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  goBtn: {
    background: '#21262d',
    color: '#e6edf3',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '9px 18px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  error: {
    background: '#da363322',
    border: '1px solid #da3633',
    color: '#f85149',
    padding: '10px',
    borderRadius: '6px',
    fontSize: '14px',
    marginBottom: '16px',
  },
};

export default function Settings() {
  const navigate = useNavigate();

  const STORAGE_KEY = 'lavan_settings_form';

  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return { ...DEFAULT_FORM, ...JSON.parse(saved) };
    } catch {}
    return { ...DEFAULT_FORM };
  });

  const [chatCodeInput, setChatCodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});

  // ── Load settings from server ONLY if this browser has no local data ─────────
  // Allows Browser A and Browser B to run independent simultaneous interviews:
  //   Browser A: already has localStorage data → uses it, stays independent
  //   Browser B: fresh (no localStorage) → pulls last-saved settings from
  //   server as a starting point so form is not empty, user can edit fields
  //   for their own interview before starting.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return; // this browser already has settings — leave it alone
    } catch {}

    const token = getToken();
    if (!token) return;

    fetch(`${API_URL}/api/auth/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data || !data.settings) return;
        const serverSettings = data.settings;
        const hasData = Object.values(serverSettings).some(
          v => v && String(v).trim() !== '' && v !== '10' && v !== '1st Round'
        );
        if (hasData) {
          const merged = { ...DEFAULT_FORM, ...serverSettings };
          setForm(merged);
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  const requiredFields = ['candidateName', 'presentRole', 'interviewingRole', 'interviewingClient', 'currentClient', 'previousClient', 'jobDescription'];
  const isFieldInvalid = (name) => requiredFields.includes(name) && touched[name] && !form[name]?.trim();
  const inputStyle = (name) => ({
    ...styles.input,
    borderColor: isFieldInvalid(name) ? '#ef4444' : '#30363d',
    boxShadow: isFieldInvalid(name) ? '0 0 0 2px rgba(239,68,68,0.25)' : 'none',
  });

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    setForm(updated);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  // ── Save settings to server ──────────────────────────────────────────────────
  const saveSettingsToServer = async (data) => {
    try {
      await fetch(`${API_URL}/api/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(data),
      });
    } catch {}
  };

  const handleStart = async (e) => {
    e.preventDefault();
    // Mark all required fields as touched to show errors
    const allTouched = {};
    requiredFields.forEach(f => { allTouched[f] = true; });
    setTouched(allTouched);
    const missing = requiredFields.filter(f => !form[f]?.trim());
    if (missing.length > 0) {
      setError('Please fill in all required fields highlighted in red.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Save settings to server before creating session
      await saveSettingsToServer(form);

      const res = await fetch(`${API_URL}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create session');
      navigate(`/chat/${data.chatCode}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToChat = () => {
    const code = chatCodeInput.trim();
    if (!code) return;
    if (!/^\d{6}$/.test(code)) {
      setError('Chat code must be exactly 6 digits.');
      return;
    }
    navigate(`/chat/${code}`);
  };

  return (
    <div style={styles.container}>
      {/* Hamburger (top-right) — matches reference */}
      <button
        style={styles.menuBtn}
        title="Menu"
        onClick={() => navigate('/dashboard')}
      >
        ☰
      </button>

      <div style={styles.wrapper}>
        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleStart}>
          <div style={styles.card}>
            {/* Title */}
            <div style={styles.cardTitle}>
              <span>⚙️</span> Interview Settings
            </div>

            <div style={styles.grid}>
              {/* Row 1 */}
              <div>
                <label style={styles.label}>Candidate Name</label>
                <input
                  style={inputStyle('candidateName')}
                  name="candidateName"
                  value={form.candidateName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter candidate name"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Present Role</label>
                <input
                  style={inputStyle('presentRole')}
                  name="presentRole"
                  value={form.presentRole}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter present role"
                  required
                />
              </div>

              {/* Row 2 */}
              <div>
                <label style={styles.label}>Interviewing Role</label>
                <input
                  style={inputStyle('interviewingRole')}
                  name="interviewingRole"
                  value={form.interviewingRole}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter interviewing role"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Interviewing Client</label>
                <input
                  style={inputStyle('interviewingClient')}
                  name="interviewingClient"
                  value={form.interviewingClient}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter interviewing client name"
                  required
                />
              </div>

              {/* Row 3 */}
              <div>
                <label style={styles.label}>Current Client</label>
                <input
                  style={inputStyle('currentClient')}
                  name="currentClient"
                  value={form.currentClient}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter current client"
                />
              </div>
              <div>
                <label style={styles.label}>Previous Client</label>
                <input
                  style={inputStyle('previousClient')}
                  name="previousClient"
                  value={form.previousClient}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter previous client"
                />
              </div>

              {/* Row 3b — Previous Client 2 */}
              <div>
                <label style={styles.label}>Previous Client 2</label>
                <input
                  style={styles.input}
                  name="previousClient2"
                  value={form.previousClient2}
                  onChange={handleChange}
                  placeholder="Enter previous client 2 (optional)"
                />
              </div>
              <div>
                <label style={styles.label}>Years of Experience</label>
                <input
                  style={styles.input}
                  name="yearsOfExperience"
                  type="number"
                  min="1"
                  max="40"
                  value={form.yearsOfExperience}
                  onChange={handleChange}
                  placeholder="e.g. 10"
                />
              </div>

              {/* Row 4 — full width */}
              <div style={styles.fieldFull}>
                <label style={styles.label}>Interview type</label>
                <select
                  style={styles.select}
                  name="interviewType"
                  value={form.interviewType}
                  onChange={handleChange}
                >
                  {interviewTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Row 5 — full width */}
              <div style={styles.fieldFull}>
                <label style={styles.label}>Job Description</label>
                <textarea
                  style={{...styles.textarea, borderColor: isFieldInvalid('jobDescription') ? '#ef4444' : '#30363d', boxShadow: isFieldInvalid('jobDescription') ? '0 0 0 2px rgba(239,68,68,0.25)' : 'none'}}
                  name="jobDescription"
                  value={form.jobDescription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Paste job description here"
                  required
                />
              </div>
            </div>

            {/* Start Interview — right-aligned blue button */}
            <div style={styles.btnRow}>
              <button
                type="submit"
                style={{ ...styles.startBtn, opacity: loading ? 0.65 : 1 }}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Start Interview'}
              </button>
            </div>

            {/* OR divider */}
            <div style={styles.orRow}>
              <span style={styles.orLine} />
              <span>OR</span>
              <span style={styles.orLine} />
            </div>
            <div style={styles.codeHint}>Use chat code to go to chat directly</div>

            {/* Chat Code row */}
            <div style={styles.codeRow}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={chatCodeInput}
                onChange={(e) => setChatCodeInput(e.target.value)}
                placeholder="Chat Code"
                maxLength={6}
              />
              <button type="button" style={styles.goBtn} onClick={handleGoToChat}>
                Go To Chat
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
